import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import themeProps from "theme/themes";
import Button from "components/Button";
import { useAppSelector } from "store/hooks";
import {
  getOpenRoleNames,
  getOpenRoles,
  isMember,
  isOwner,
} from "utils/projectUtils";
import IProject from "common/interfaces/IProject";
import { patch } from "core/Api";
import Select from "components/Select";

interface ProjectRolesProps {
  project: IProject;
}

const Wrapper = styled.div``;
const Title = styled.h2`
  margin-bottom: 24px;
`;

const Heading = styled.h3`
  margin-top: 40px;
  margin-bottom: 10px;
  font-size: 1.4rem;
`;

const Text = styled.p`
  font-size: 1.2rem;
`;

const EntriesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const ListItem = styled.div`
  font-size: 1.2rem;
  border: 1px solid ${(props: themeProps) => props.theme.border.secondary};
  display: flex;
  align-items: center;
  justify-items: space-between;
  padding: 8px 20px;
  border-radius: 8px;
  margin-right: 20px;
  margin-bottom: 20px;
`;

const InfoWrapper = styled.div`
  width: 100%;
  border: 1px solid ${(props: themeProps) => props.theme.border.secondary};
  border-radius: 12px;
  padding: 16px 20px;
`;

const ButtonWrapper = styled.div`
  max-width: 250px;
  margin: 0 auto;
`;

const ProjectRoles = ({ project }: ProjectRolesProps) => {
  const { t } = useTranslation("project");
  const [role, setRole] = useState(
    getOpenRoles(project) ? getOpenRoles(project)[0] : null
  );
  const [selected, setSelected] = useState<string>(
    project ? (project.roles ? getOpenRoleNames(project)[0] : "") : ""
  );
  const userId = useAppSelector((state) => state.user.auth.userId);

  useEffect(() => {
    const foundRole = project?.roles?.find((role) => role.name === selected);
    foundRole && setRole(foundRole);
  }, [selected, project?.roles]);

  const apply = async () => {
    try {
      await patch(`/projects/roles/${role?._id}/application/${userId}`);
    } catch (e: any) {
      console.log(e);
    }
  };

  return (
    <Wrapper>
      <Title>{t("openVacancies")}</Title>
      <Select
        selected={selected}
        options={getOpenRoleNames(project)}
        handleChange={setSelected}
      />
      <Heading>{t("roleDescription")}</Heading>
      <InfoWrapper>
        <Text>{role?.description}</Text>
      </InfoWrapper>
      <Heading>{t("roleRequirements")}</Heading>
      <EntriesWrapper>
        {role?.requirements.map((requirement) => (
          <ListItem key={requirement}>{requirement}</ListItem>
        ))}
      </EntriesWrapper>
      {!isMember(project, userId) && !isOwner(project, userId) ? (
        <ButtonWrapper>
          <Button
            filled
            label={t("apply")}
            styles={{ marginTop: "50px" }}
            onClick={apply}
          />
        </ButtonWrapper>
      ) : null}
    </Wrapper>
  );
};

export default ProjectRoles;
